import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'

/**
 * A generic owner's credit balance, denominated in its own currency, tenant-isolated via
 * the inherited `tenant` field. The currency is set once at account creation (first
 * grant/seed) from an explicit override or the app's `defaultCurrency`, and is immutable
 * thereafter (no mixed-currency balances).
 */
@ObjectType()
@InputType('CreditAccountInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'aicreditaccounts' } })
export class CreditAccount extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('crac') })
  declare _id: string

  /**
   * Generic owner of this balance — a `User._id` (User billing scope) or a `Tenant._id`
   * (Tenant billing scope), resolved by `resolveBillingOwnerId`. Unique per tenant — see
   * CreditAccountSchema.
   */
  @Field(() => String)
  @Prop({ type: String, required: true })
  ownerId: string

  /** Fixed at creation; never re-denominated. */
  @Field(() => String)
  @Prop({ type: String, required: true })
  currency: string

  @Field(() => Float)
  @Prop({ type: Number, required: true, default: 0 })
  balance: number

  /**
   * `chargeKey`s already claimed against this account by `DebitCredits.claimAndDebit` — the
   * exactly-once gate for a caller that may retry the same billable unit of work (a BullMQ batch
   * chunk). Checked and appended in the SAME atomic `findOneAndUpdate` that applies the balance
   * `$inc`, so at most one caller's claim for a given `chargeKey` can ever also move the balance
   * — no unique index needed, since the conditional update runs against THIS account's own
   * document, which already exists by the time any `chargeKey`-bearing debit runs (see
   * `DebitCredits.ts`). Not exposed via GraphQL — internal bookkeeping only.
   *
   * Grows for the life of the account with every batch-chunk debit it ever claims; nothing
   * evicts an entry, deliberately — capping or expiring it would let a late retry re-claim an
   * already-billed `chargeKey` and silently reopen the double-debit race this field exists to
   * close. Accepted as a disclosed storage-growth tradeoff, not a correctness gap.
   */
  @Prop({ type: [String], required: true, default: [] })
  claimedChargeKeys: string[]
}
